import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, parseJsonSafe } from '../../utils/api';
import { todayStr } from '../../utils/invoiceUtils';

const METHOD_OPTIONS = [
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'domiciliacion', label: 'Domiciliación' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'otro', label: 'Otro' }
];

const SOURCE_BADGE = {
  manual: { label: 'Manual', cls: 'bg-secondary' },
  stripe: { label: 'Stripe', cls: 'bg-info text-dark' },
  dolibarr: { label: 'Dolibarr', cls: 'bg-primary' }
};

const InvoicePayments = ({ invoiceId, invoiceTotal, dolibarrLinked, onChanged }) => {
  const token = localStorage.getItem('token');
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const paid = useMemo(
    () => payments.reduce((acc, p) => acc + Number(p.amount || 0), 0),
    [payments]
  );
  const pending = Math.max(0, Number(invoiceTotal || 0) - paid);

  const [form, setForm] = useState({
    amount: '',
    payment_date: todayStr(),
    method: 'transferencia',
    reference: '',
    notes: '',
    sync_dolibarr: false
  });

  const load = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/invoices/${invoiceId}/payments`, { headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.error || data.message || 'Error al cargar pagos');
      setPayments(Array.isArray(data.payments) ? data.payments : []);
      setSummary(data.summary || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const openModal = () => {
    setForm({
      amount: pending > 0 ? pending.toFixed(2) : '',
      payment_date: todayStr(),
      method: 'transferencia',
      reference: '',
      notes: '',
      sync_dolibarr: Boolean(dolibarrLinked)
    });
    setShowModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await apiFetch(`/api/invoices/${invoiceId}/payments`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Number(form.amount),
          payment_date: form.payment_date,
          method: form.method,
          reference: form.reference || null,
          notes: form.notes || null,
          sync_dolibarr: form.sync_dolibarr
        })
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.error || data.message || 'Error al registrar pago');
      setShowModal(false);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (paymentId) => {
    if (!window.confirm('¿Eliminar este pago?')) return;
    try {
      const res = await apiFetch(`/api/invoices/${invoiceId}/payments/${paymentId}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.error || data.message || 'Error al eliminar');
      if (data.dolibarrWarning) alert(data.dolibarrWarning);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      alert(err.message);
    }
  };

  if (!invoiceId) return null;

  return (
    <div className="card mt-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>
          <strong>Pagos</strong>
          {summary && (
            <span className="ms-3 text-muted small">
              Total: {Number(invoiceTotal || 0).toFixed(2)} € · Pagado: {paid.toFixed(2)} € · Pendiente: {pending.toFixed(2)} €
            </span>
          )}
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={openModal}>
          + Registrar pago
        </button>
      </div>
      <div className="card-body p-0">
        {error && <div className="alert alert-danger m-3">{error}</div>}
        {loading ? (
          <div className="p-3 text-muted">Cargando…</div>
        ) : payments.length === 0 ? (
          <div className="p-3 text-muted">Sin pagos registrados.</div>
        ) : (
          <table className="table mb-0">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Importe</th>
                <th>Método</th>
                <th>Referencia</th>
                <th>Origen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => {
                const badge = SOURCE_BADGE[p.source] || SOURCE_BADGE.manual;
                return (
                  <tr key={p.id}>
                    <td>{p.payment_date ? String(p.payment_date).slice(0, 10) : '-'}</td>
                    <td>{Number(p.amount).toFixed(2)} €</td>
                    <td>{p.method || '-'}</td>
                    <td>{p.reference || '-'}</td>
                    <td>
                      <span className={`badge ${badge.cls}`}>{badge.label}</span>
                      {p.dolibarr_payment_id && <span className="badge bg-success ms-1">Sync</span>}
                    </td>
                    <td className="text-end">
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(p.id)}>
                        Eliminar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={submit}>
              <div className="modal-header">
                <h5 className="modal-title">Registrar pago</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Importe (€)</label>
                  <input
                    type="number" step="0.01" min="0.01" required
                    className="form-control"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Fecha</label>
                  <input
                    type="date" required
                    className="form-control"
                    value={form.payment_date}
                    onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Método</label>
                  <select
                    className="form-select"
                    value={form.method}
                    onChange={(e) => setForm({ ...form, method: e.target.value })}
                  >
                    {METHOD_OPTIONS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Referencia</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.reference}
                    onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Notas</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>
                {dolibarrLinked && (
                  <div className="alert alert-info py-2 mb-0 small">
                    Esta factura proviene de Dolibarr (fuente de verdad). El pago se registrará también en Dolibarr automáticamente.
                  </div>
                )}
                {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePayments;
